import { ExternalLink, KeyRound, Loader2, Save } from 'lucide-react';

import type { ClaudeConnection } from '../lib/claudeConnection';

/**
 * Les commandes de la connexion Claude : le bouton d'appel, puis, une fois la
 * session ouverte, le lien d'autorisation et le champ du code.
 *
 * Séparé de l'état pour être posé tel quel dans l'écran Admin comme dans la
 * fenêtre d'accueil, sans que l'un ait à réimplémenter le parcours de l'autre.
 */
export function ClaudeConnectFlow({
    connection,
    libelleDemarrage,
    onCancel,
}: {
    connection: ClaudeConnection;
    libelleDemarrage: string;
    /** Bouton de retrait, quand l'écran hôte en veut un. */
    onCancel?: () => void;
}) {
    const { session, code, setCode, starting, submitting } = connection;

    if (!session) {
        return (
            <div className="space-y-2">
                {connection.verifying && (
                    <p className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Jeton enregistré. Essai en cours : il lance une génération
                        minuscule, et peut demander deux minutes si le jeton est refusé.
                    </p>
                )}
                {!connection.verifying && connection.verification && !connection.verification.ok && (
                    <p className="rounded-lg border border-[var(--color-warning)] px-3 py-2 text-xs text-[var(--color-warning)]">
                        {connection.verification.error}
                    </p>
                )}
                {!connection.verifying && connection.verification?.ok && (
                    <p className="text-xs text-[var(--color-success)]">
                        Jeton enregistré et essayé avec succès.
                    </p>
                )}
                <div className="flex flex-wrap gap-2">
                    <button
                        className="btn-primary"
                        disabled={starting || connection.verifying}
                        onClick={connection.start}
                    >
                        {starting
                            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            : <KeyRound className="mr-2 h-4 w-4" />}
                        {starting ? 'Ouverture de l’autorisation…' : libelleDemarrage}
                    </button>
                    {onCancel && (
                        <button className="btn-secondary" onClick={onCancel}>Plus tard</button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-2 rounded-lg border border-[var(--color-border)] p-3">
            <p className="text-xs text-[var(--color-text-secondary)]">
                Autorisez Clippit AVV sur votre compte Claude, puis recopiez ici le code
                affiché. Un onglet a normalement dû s'ouvrir ; s'il ne l'a pas fait, votre
                navigateur l'a bloqué et ce lien le remplace.
            </p>
            <a
                className="btn-secondary inline-flex py-1 text-xs"
                href={session.url}
                target="_blank"
                rel="noopener noreferrer"
            >
                <ExternalLink className="mr-2 h-3.5 w-3.5" />
                Ouvrir l'autorisation
            </a>
            <div className="flex flex-wrap gap-2">
                <input
                    className="input flex-1 min-w-[240px] font-mono text-xs"
                    placeholder="Code d'autorisation"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    // Entrée vaut validation : le code arrive par un collage,
                    // et chercher le bouton juste après est une étape de trop.
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && code.trim().length >= 8 && !submitting) {
                            connection.submit();
                        }
                    }}
                    autoFocus
                />
                <button
                    className="btn-primary"
                    disabled={code.trim().length < 8 || submitting}
                    onClick={connection.submit}
                >
                    {submitting
                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        : <Save className="mr-2 h-4 w-4" />}
                    {submitting ? 'Vérification…' : 'Valider'}
                </button>
                <button className="btn-secondary" onClick={connection.cancel}>
                    Annuler
                </button>
            </div>
            <p className="text-xs text-[var(--color-text-muted)]">
                La session d'autorisation expire au bout de dix minutes.
            </p>
        </div>
    );
}
